import {
  ChronoField,
  ChronoUnit,
  DateTimeFormatter,
  DateTimeFormatterBuilder,
  Duration,
  LocalDate,
  LocalDateTime,
  Period,
  TemporalAccessor,
  ZonedDateTime,
  ZoneId,
} from '@js-joda/core';
import { Locale } from '@js-joda/locale';
import '@js-joda/locale_en-us';
import '@js-joda/timezone';
import pluralize from 'pluralize';
import React from 'react';
import { LastActivityBasicsFragment } from '../graphql/types';

const dayOrdinals: Record<number, string> = {};
for (let i = 1; i <= 31; i += 1) {
  dayOrdinals[i] = i.toString();
}

export const getMostRecentActivityDate = (
  lastActivity: LastActivityBasicsFragment | null = {},
) => {
  if (!lastActivity) {
    return null;
  }

  const { heart, heartWeb } = lastActivity;

  const heartDate = heart ? ZonedDateTime.parse(heart) : null;
  const heartWebDate = heartWeb ? ZonedDateTime.parse(heartWeb) : null;

  if (!heartDate && !heartWebDate) {
    return null;
  }

  if (!heartDate) {
    return heartWebDate;
  }

  if (!heartWebDate) {
    return heartDate;
  }

  return heartDate.isAfter(heartWebDate) ? heartDate : heartWebDate;
};

export const getAgeFromBirthday = (
  birthday: string | null | undefined,
): string | null => {
  if (!birthday) {
    return null;
  }
  let bday;
  try {
    bday = LocalDate.parse(birthday);
  } catch {
    return null;
  }
  const today = LocalDate.now();
  const age = Period.between(bday, today);
  if (age.isNegative()) {
    return null;
  }
  const years = age.years();
  return `${years} ${years === 1 ? 'year' : 'years'} old`;
};

export const parseNullableZdt = (
  date: string | ZonedDateTime | null | undefined,
) => {
  if (!date) {
    return null;
  }
  return parseZdt(date);
};

const parseZdt = (date: string | ZonedDateTime) =>
  typeof date === 'string' ? ZonedDateTime.parse(date) : date;

const parseLocalDate = (date: string | LocalDate) =>
  typeof date === 'string' ? LocalDate.parse(date) : date;

const applyDateFormat =
  (fmt: DateTimeFormatter) => (date: string | TemporalAccessor) => {
    const temporal =
      typeof date === 'string'
        ? ZonedDateTime.parse(date).withZoneSameInstant(ZoneId.SYSTEM)
        : date;
    return fmt.format(temporal);
  };

const shortYearDateFormat = DateTimeFormatter.ofPattern('MM/dd/yy').withLocale(
  Locale.US,
);

const getDateSuffix = (date: ZonedDateTime | LocalDate) => {
  const dayOfMonth = date.dayOfMonth();
  if (dayOfMonth >= 11 && dayOfMonth <= 13) {
    return 'th';
  }
  switch (dayOfMonth % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const shortYearDate = applyDateFormat(shortYearDateFormat);

const monthDayOrdinalYearFormat = DateTimeFormatter.ofPattern(
  'MMM dd, yyyy',
).withLocale(Locale.US);

const monthDayOrdinalYearLongFormat = DateTimeFormatter.ofPattern(
  'EEEE, MMM dd, yyyy',
).withLocale(Locale.US);

const dayMonthDayFormat = DateTimeFormatter.ofPattern(
  'EEEE, MMMM d',
).withLocale(Locale.US);

const monthDayFormat = DateTimeFormatter.ofPattern('MMMM d').withLocale(
  Locale.US,
);

const monthYearFormat = DateTimeFormatter.ofPattern('MMMM yyyy').withLocale(
  Locale.US,
);

const dateTimeLongFormFormat = DateTimeFormatter.ofPattern(
  'EEE, MMM dd · h:mm a',
).withLocale(Locale.US);

const dateTimeLongFormWithZoneFormat = DateTimeFormatter.ofPattern(
  'EEE, MMM dd · h:mm a · z',
).withLocale(Locale.US);

const monthDayYearTimeLongFormFormat = DateTimeFormatter.ofPattern(
  'EEE MMM dd, YYYY · h:mm a',
).withLocale(Locale.US);

export const monthYear = applyDateFormat(monthYearFormat);

export const monthDayOrdinalYear = applyDateFormat(monthDayOrdinalYearFormat);

export const monthDayOrdinalYearLong = applyDateFormat(
  monthDayOrdinalYearLongFormat,
);

const dayMonthDay = applyDateFormat(dayMonthDayFormat);

const monthDay = applyDateFormat(monthDayFormat);

export const monthDayWithSuffix = (date: LocalDate | string) =>
  monthDay(date) + getDateSuffix(parseLocalDate(date));

export const dayMonthDayWithSuffix = (date: LocalDate | string) =>
  dayMonthDay(date) + getDateSuffix(parseLocalDate(date));

// Nov 26
const shortMonthDayFormat = DateTimeFormatter.ofPattern('MMM d').withLocale(
  Locale.US,
);
export const shortMonthDay = applyDateFormat(shortMonthDayFormat);

export const dateTimeLongForm = applyDateFormat(dateTimeLongFormFormat);

const resolveZone = (zone?: string | null) =>
  zone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

/** The same instant, read in `zone` (the browser's when omitted). */
export const inZone = (date: string, zone?: string | null): ZonedDateTime =>
  ZonedDateTime.parse(date).withZoneSameInstant(ZoneId.of(resolveZone(zone)));

/**
 * Daylight-aware short name for `zone` at that instant, e.g. "EDT".
 *
 * The instant is taken through js-joda rather than `new Date(date)`: an API
 * ZonedDateTime serializes as `…-04:00[America/New_York]`, which `Date` cannot
 * parse and would leave as an Invalid Date.
 */
export const shortTimeZoneName = (
  date: string,
  zone?: string | null,
): string => {
  const resolvedZone = resolveZone(zone);

  return (
    new Intl.DateTimeFormat('en-US', {
      timeZone: resolvedZone,
      timeZoneName: 'short',
    })
      .formatToParts(
        new Date(inZone(date, resolvedZone).toInstant().toEpochMilli()),
      )
      .find((p) => p.type === 'timeZoneName')?.value ?? resolvedZone
  );
};

export const dateTimeLongFormWithZone = (
  date: string,
  zone?: string | null,
): string => {
  const zdt = inZone(date, zone);
  const formatted = dateTimeLongFormWithZoneFormat.format(zdt);

  // js-joda falls back to the full IANA zone ID (contains '/') when no CLDR
  // short generic name exists (e.g. America/Sao_Paulo → no "BRT" entry).
  // Use Intl as a fallback to get the proper short abbreviation.
  const lastSep = formatted.lastIndexOf('·');
  if (lastSep !== -1 && formatted.slice(lastSep).includes('/')) {
    return `${dateTimeLongFormFormat.format(zdt)} · ${shortTimeZoneName(date, zone)}`;
  }

  return formatted;
};

/**
 * e.g. "Mon, Jul 15 · 9:00 AM · EDT", read in `zone`.
 *
 * Not `dateTimeLongFormWithZone`: that prefers js-joda's generic name ("ET"),
 * and a coach reading a time in a zone they are not in needs the
 * daylight-specific one to read it unambiguously.
 */
export const dateTimeLongFormWithDaylightZone = (
  date: string,
  zone?: string | null,
): string =>
  `${dateTimeLongForm(inZone(date, zone))} · ${shortTimeZoneName(date, zone)}`;

// The end is dated too when it falls on a later day than the start.
const rangeEndLabel = (startZdt: ZonedDateTime, endZdt: ZonedDateTime) =>
  startZdt.toLocalDate().equals(endZdt.toLocalDate())
    ? time(endZdt)
    : dateTimeLongForm(endZdt);

/** e.g. "Mon, Jul 15 · 9:00 AM – 10:00 AM · EDT", read in `zone`. */
export const dateTimeRangeWithDaylightZone = (
  start: string,
  end: string,
  zone?: string | null,
): string => {
  const startZdt = inZone(start, zone);
  const endLabel = rangeEndLabel(startZdt, inZone(end, zone));

  return `${dateTimeLongForm(startZdt)} \u2013 ${endLabel} \u00b7 ${shortTimeZoneName(start, zone)}`;
};

export const timeRangeWithDaylightZone = (
  start: string,
  end: string,
  zone?: string | null,
): string => {
  const startZdt = inZone(start, zone);
  const endLabel = rangeEndLabel(startZdt, inZone(end, zone));

  return `${time(startZdt)} \u2013 ${endLabel} \u00b7 ${shortTimeZoneName(start, zone)}`;
};

export const monthDayOrdinalYearLongWithZone = (
  date: string,
  zone?: string | null,
): string => monthDayOrdinalYearLongFormat.format(inZone(date, zone));

export const monthDayYearTimeLongForm = applyDateFormat(
  monthDayYearTimeLongFormFormat,
);

export const dayMonthYearWithTimeZone = (date: ZonedDateTime | string) => {
  const zdt = parseZdt(date);
  const dateSuffix = getDateSuffix(zdt);

  return DateTimeFormatter.ofPattern(
    `EEEE, MMMM dd'${dateSuffix}', 'at' h:mm a (ZZZZ)`,
  )
    .withLocale(Locale.US)
    .format(zdt);
};

const fullDateNoYearFormat = new DateTimeFormatterBuilder()
  .appendPattern('eeee, MMMM ')
  .appendText(ChronoField.DAY_OF_MONTH, dayOrdinals)
  .toFormatter()
  .withLocale(Locale.US);

export const fullDateNoYear = (date: string | LocalDate) =>
  applyDateFormat(fullDateNoYearFormat)(date) +
  getDateSuffix(parseLocalDate(date));

const weekdayFormat = new DateTimeFormatterBuilder()
  .appendPattern('EEEE')
  .toFormatter()
  .withLocale(Locale.US);

const weekday = applyDateFormat(weekdayFormat);

const shortDateNoYearFormat = new DateTimeFormatterBuilder()
  .appendPattern('MMM d')
  .toFormatter()
  .withLocale(Locale.US);

export const shortDateNoYear = applyDateFormat(shortDateNoYearFormat);

const shortDateOfMonthFormat = new DateTimeFormatterBuilder()
  .appendPattern('d')
  .toFormatter()
  .withLocale(Locale.US);

export const shortDateOfMonth = applyDateFormat(shortDateOfMonthFormat);

const shortDateYearFormat = new DateTimeFormatterBuilder()
  .appendPattern('MMM d')
  .appendPattern(', yyyy')
  .toFormatter()
  .withLocale(Locale.US);

export const shortDateYear = applyDateFormat(shortDateYearFormat);

const shortDayMonthDateYearFormat = new DateTimeFormatterBuilder()
  .appendPattern('E, MMM d, yyyy')
  .toFormatter()
  .withLocale(Locale.US);

export const shortDayMonthDateYear = applyDateFormat(
  shortDayMonthDateYearFormat,
);

const shortDateNoYearTimeFormat = new DateTimeFormatterBuilder()
  .appendPattern('MMM d')
  .appendLiteral(' at ')
  .appendPattern('h:mm a')
  .toFormatter()
  .withLocale(Locale.US);

export const shortDateNoYearTime = applyDateFormat(shortDateNoYearTimeFormat);

/**
 * F O R M A T T E R S
 */

// 7:47 PM
const timeOfDayWithSpace = DateTimeFormatter.ofPattern('h:mm a').withLocale(
  Locale.US,
);

export const time = applyDateFormat(timeOfDayWithSpace);

// Oct 22, 2001
export const monthDayYear = DateTimeFormatter.ofPattern('MMM d, y').withLocale(
  Locale.US,
);

// Tuesday, January 9th, 2024
export const dayMonthDateYear = DateTimeFormatter.ofPattern(
  'EEEE, MMMM d, y',
).withLocale(Locale.US);
export const getLocalizedDayMonthDateYear = (date: string) =>
  ZonedDateTime.parse(date)
    .withZoneSameInstant(ZoneId.SYSTEM)
    .format(dayMonthDateYear)
    .toString();

const monthDayYearWithSlashesFormat = DateTimeFormatter.ofPattern(
  'MM/dd/yyyy',
).withLocale(Locale.US);

export const monthDayYearWithSlashes = applyDateFormat(
  monthDayYearWithSlashesFormat,
);

const monthDayWithSlashesFormat = DateTimeFormatter.ofPattern(
  'MM/dd',
).withLocale(Locale.US);

export const monthDayWithSlashes = applyDateFormat(monthDayWithSlashesFormat);

/**
 * T I M E Z O N E   S H E N A N I G A N S
 *
 * ZonedDateTime.now()                  = "2021-05-14T12:23:56.586-04:00[America/New_York]"
 * .withZoneSameInstant(ZoneId.SYSTEM)  = "2021-05-14T12:23:56.586-04:00[SYSTEM]"
 */

export const stringToZonedDateTime = (dateTime: string) =>
  ZonedDateTime.parse(dateTime).withZoneSameInstant(ZoneId.SYSTEM);

export const stringToLocalDate = (dateTime: string) =>
  ZonedDateTime.parse(dateTime)
    .withZoneSameInstant(ZoneId.SYSTEM)
    .toLocalDate();

export const formatLongChatDate = (
  chatDate: LocalDate | string,
  today = LocalDate.now(),
): string => {
  const parsedChatDate =
    typeof chatDate === 'string'
      ? ZonedDateTime.parse(chatDate).toLocalDate()
      : chatDate;
  const days = parsedChatDate.until(today, ChronoUnit.DAYS);
  if (days === 0) {
    return 'Today';
  } else if (days === 1) {
    return 'Yesterday';
  } else if (days > 1 && days < 7) {
    return weekday(chatDate);
  } else {
    return shortDayMonthDateYear(chatDate);
  }
};

export const getTimeSince = (
  date?: LocalDate,
  today: LocalDate = LocalDate.now(),
): string => {
  const unknown = 'unknown';
  try {
    if (!date) {
      return unknown;
    }

    const years = date.until(today, ChronoUnit.YEARS);
    const months = date.until(today, ChronoUnit.MONTHS);
    const weeks = date.until(today, ChronoUnit.WEEKS);
    const days = date.until(today, ChronoUnit.DAYS);

    if (years > 0) {
      return `${pluralize('year', years, true)} ago`;
    } else if (months > 0) {
      return `${pluralize('month', months, true)} ago`;
    } else if (weeks > 0) {
      return `${pluralize('week', weeks, true)} ago`;
    } else if (days === 0) {
      return 'Today';
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days > 0) {
      return `${days} days ago`;
    } else if (days === -1) {
      return 'Tomorrow';
    } else if (days < -1) {
      return date.format(monthDayYear);
    } else {
      return unknown;
    }
  } catch {
    return unknown;
  }
};

export const getTimeUntilMillis = (date: ZonedDateTime) => {
  const td = ZonedDateTime.now();
  const timeBetween = ChronoUnit.MILLIS.between(td, date);
  return timeBetween;
};

export const isLocalDateInPast = (date: string): boolean => {
  const localDate = LocalDate.parse(date);
  const now = LocalDate.now();

  return localDate.isBefore(now);
};

export const isLocalDateTodayOrInFuture = (date: string): boolean => {
  const localDate = LocalDate.parse(date);
  const now = LocalDate.now();

  return localDate.isAfter(now) || localDate.equals(now);
};

export const isLocalDateTodayOrInPast = (date: string): boolean => {
  const localDate = LocalDate.parse(date);
  const now = LocalDate.now();

  return localDate.isBefore(now) || localDate.equals(now);
};

export const isLocalDateWithinDateRange = (dateRange: {
  start: LocalDate;
  end: LocalDate;
}) => {
  const isAfterStart = isLocalDateTodayOrInPast(dateRange.start.toString());
  const isBeforeEnd = isLocalDateTodayOrInFuture(dateRange.end.toString());
  return isAfterStart && isBeforeEnd;
};

export const dateInPast = (date: string): boolean => {
  const zonedDate = stringToZonedDateTime(new Date(date).toISOString());
  const now = ZonedDateTime.now();
  if (zonedDate === null) {
    throw new Error(`Cannot compare against invalid date: ${date}`);
  }
  return zonedDate.isBefore(now);
};

export const NEW_MEMBER_DAYS_THRESHOLD = 14;

// Note on memoization - we only want this refreshed once a day; if someone keeps their tab open for days, this should refresh accordingly
export const useNewMembersDaysAgo = (
  now: ZonedDateTime = ZonedDateTime.now(),
) => {
  const daysAgo = React.useMemo(
    () =>
      now.minusDays(NEW_MEMBER_DAYS_THRESHOLD).withFixedOffsetZone().toString(),
    [now.minusDays],
  );
  return daysAgo;
};

// get coaches time zone and convert it to UTC so we can get "todays" info
export const inclusiveDateRange = (dateRange?: {
  start?: string | null;
  end?: string | null;
}) => ({
  start: dateRange?.start
    ? ZonedDateTime.parse(dateRange.start)
        .withZoneSameInstant(ZoneId.SYSTEM)
        .withHour(0)
        .withMinute(0)
        .withSecond(0)
        .withFixedOffsetZone()
        .toJSON()
    : undefined,
  end: dateRange?.end
    ? ZonedDateTime.parse(dateRange.end)
        .withZoneSameInstant(ZoneId.SYSTEM)
        .withHour(23)
        .withMinute(59)
        .withSecond(59)
        .withFixedOffsetZone()
        .toJSON()
    : undefined,
});

export const getLocalTimeUtcStartOfDay = (day = LocalDateTime.now()) => {
  const localNow = day.atZone(ZoneId.SYSTEM);
  const localStartOfDay = localNow.toLocalDate().atStartOfDay();
  const localZdtStartOfDay = ZonedDateTime.parse(
    localStartOfDay.atZone(ZoneId.SYSTEM).toString(),
  );
  const utcStartOfDay = localZdtStartOfDay.withZoneSameInstant(ZoneId.UTC);
  return utcStartOfDay;
};
export const getLocalTimeUtcEndOfDay = (day = LocalDateTime.now()) => {
  const localNow = day.atZone(ZoneId.SYSTEM);
  const localEndOfDay = localNow
    .toLocalDate()
    .atStartOfDay()
    .plusDays(1)
    .minusSeconds(1);
  const localZdtEndOfDay = ZonedDateTime.parse(
    localEndOfDay.atZone(ZoneId.SYSTEM).toString(),
  );
  const utcEndOfDay = localZdtEndOfDay.withZoneSameInstant(ZoneId.UTC);
  return utcEndOfDay;
};

export const getStartOfTime = (now = ZonedDateTime.now()) => {
  const startOfTime = now.withYear(1970).withDayOfYear(1).withFixedOffsetZone();
  return startOfTime;
};

export const getEndOfTime = (now = ZonedDateTime.now()) => {
  const endOfTime = now.plusYears(100).withDayOfYear(365).withFixedOffsetZone();
  return endOfTime;
};

export const getAttendanceTrackingReleaseDate = () =>
  getLocalTimeUtcStartOfDay()
    .withYear(2023)
    .withMonth(8)
    .withDayOfMonth(1)
    .toString();

export const getBrowserTimezone = (): string => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    ZoneId.of(zone);
    return zone;
  } catch {
    return 'UTC';
  }
};

export const getDisplayLabelForTimezoneWithOffset = (zoneId: string) => {
  const zone = zoneId.split('/');
  const offset = ZonedDateTime.now(ZoneId.of(zoneId)).offset().toString();
  return `${zone[zone.length - 1].replace('_', ' ')} (GMT ${offset})`;
};

export function getListOfLocalAvailableTimeZones() {
  const tzList = ZoneId.getAvailableZoneIds();

  const displayableTimezones = tzList.map((tz) => {
    const label = getDisplayLabelForTimezoneWithOffset(tz);
    return {
      value: tz,
      label,
    };
  });

  return displayableTimezones;
}

export const minutesToFormattedHoursAndMinutes = (inputMinutes: number) => {
  const duration = Duration.ofMinutes(inputMinutes);
  const hours = duration.toHours();
  const minutes = duration.toMinutes() % 60;
  if (hours < 1) {
    return `${minutes} min`;
  }

  return `${hours} hr${minutes > 0 ? ` ${minutes} min` : ''}`;
};

export const minutesToMinutesAndSeconds = (inputMinutes: number) => {
  const wholeMinutes = Math.floor(inputMinutes);
  const fractionalMinutes = inputMinutes - wholeMinutes;
  const seconds = Math.round(fractionalMinutes * 60);

  if (seconds === 0) {
    return wholeMinutes.toString();
  }
  return `${wholeMinutes}:${seconds.toString().padStart(2, '0')}`;
};

export const compareDateStrings = (
  a: string | null | undefined,
  b: string | null | undefined,
) => {
  const startOfTime = getStartOfTime();
  const parsedA = a ? ZonedDateTime.parse(a) : startOfTime;
  const parsedB = b ? ZonedDateTime.parse(b) : startOfTime;

  return parsedB.compareTo(parsedA);
};
